import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import {
  Text,
  Title3,
  Spinner,
  TabList,
  Tab,
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { CheckmarkCircle24Regular } from "@fluentui/react-icons";
import { useWorkflowIntelligence } from "../../contexts/WorkflowIntelligenceContext";
import {
  getDiscussionsBySpreadsheet,
  DiscussionDto,
} from "../../services/discussions.service";
import {
  getStatusItemsBySpreadsheet,
  StatusItemDto,
} from "../../services/status-items.service";
import {
  getReviewInfo,
  SpreadsheetReviewInfoResponse,
} from "../../services/spreadsheets.service";
import { syncWorksheetsToStatusItems } from "../../services/sync-worksheets.service";
import { ExcelService } from "../../services/excel.service";
import { isNonWorkpaperSheet } from "../../services/non-workpaper-sheets";
import { isStandaloneMode } from "../../utils/standalone-mode";
import { getUserFriendlyErrorMessage } from "../../utils/error-messages";
import { useTypographyStyles } from "../../styles/typography.styles";
import { useTrackEvent } from "../../hooks/useTrackEvent";
import ManagerTab from "./components/ManagerTab";
import PartnerTab from "./components/PartnerTab";

type TabValue = "manager" | "partner";

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalM,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    height: "100%",
    overflow: "auto",
    boxSizing: "border-box",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXXXL,
  },
  errorContainer: {
    color: tokens.colorPaletteRedForeground1,
  },
  signedBanner: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  signedContent: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalS,
    color: tokens.colorPaletteGreenForeground1,
  },
  signedText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
});

/**
 * Workbook Review page. Two tabs (Manager / Partner) cover the sign-off flow;
 * each tab gates its own controls by role. The page itself owns the data
 * fetch and shows the signed banners + tab strip.
 */
const Review: React.FC = () => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const track = useTrackEvent();
  const {
    spreadsheetId,
    userRole,
    reviewInfo,
    setReviewInfo,
    worksheets,
    refreshWorksheets,
  } = useWorkflowIntelligence();

  const [discussions, setDiscussions] = useState<DiscussionDto[]>([]);
  const [statusItems, setStatusItems] = useState<StatusItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<TabValue>("manager");
  const [isSyncingFromWarning, setIsSyncingFromWarning] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  // Why the workbook can't be written to, or null when it's fine.
  // Sign-off writes the completion certificate into this workbook, so a
  // read-only (or never-saved) workbook has to block the sign BEFORE the
  // backend commits it — otherwise the sign-off succeeds server-side
  // while Excel quietly forks the document and puts the certificate in a
  // copy the user never opened.
  const [workbookBlockReason, setWorkbookBlockReason] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!spreadsheetId) {
        setError("No spreadsheet ID available");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Re-fetch review info even though the context already has it — the
        // user may have signed in another session, and we want this page to
        // show the latest state (and push it back to the context).
        const [reviewInfoData, discussionsData, statusItemsData] = await Promise.all([
          getReviewInfo(spreadsheetId),
          getDiscussionsBySpreadsheet(spreadsheetId),
          getStatusItemsBySpreadsheet(spreadsheetId),
        ]);
        setReviewInfo(reviewInfoData);
        setDiscussions(discussionsData);
        setStatusItems(statusItemsData);
      } catch (err) {
        setError(getUserFriendlyErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [spreadsheetId, setReviewInfo]);

  // Probe the workbook's write state on mount so the Sign buttons can be
  // disabled up front with an explanation, rather than letting the user
  // fill in the checklist and only discover the problem on click. The
  // sign handlers re-check immediately before committing too — this is
  // purely the proactive half.
  useEffect(() => {
    if (isStandaloneMode()) return;
    let cancelled = false;
    (async () => {
      try {
        const state = await ExcelService.getWorkbookWriteState();
        if (cancelled || !state.supported) return;
        if (state.readOnly) {
          setWorkbookBlockReason(
            "This workbook is open in read-only mode. Sign-off writes a completion certificate " +
              "into the workbook, so it's disabled until you reopen with edit access — check whether " +
              "someone else has the file open, whether it came from an email attachment, or whether " +
              "it's marked as Final.",
          );
        } else if (!state.previouslySaved) {
          setWorkbookBlockReason(
            "This workbook hasn't been saved anywhere yet. Save it (Ctrl+S) to a proper location " +
              "before signing off, so the completion certificate is stored somewhere you can find it.",
          );
        }
      } catch {
        // Diagnostic only — never block sign-off because the probe failed.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Force a fresh worksheet list on mount. The context only refreshes on
  // add/delete/rename events — hiding a sheet doesn't trigger a refresh, so
  // the stale visible list may still include a sheet the user just hid. The
  // warning below only makes sense against the actually-visible sheets (Sync
  // Excel skips hidden ones anyway).
  useEffect(() => {
    refreshWorksheets();
  }, [refreshWorksheets]);

  const managerAlreadySigned = !!reviewInfo?.signingManagerEmail;
  const partnerAlreadySigned = !!reviewInfo?.signingPartnerEmail;
  const secondaryPartnerAlreadySigned = !!reviewInfo?.secondarySigningPartnerEmail;

  // Visible worksheets that don't have any status item. Shown to both
  // Manager and Partner tabs — anyone reviewing a workbook wants to know
  // if some sheets aren't being tracked. Sync Excel creates the missing
  // items and dismisses the warning.
  //
  // Matches syncWorksheetsToStatusItems' create logic: it treats ANY
  // existing status item (hidden or not) as "covered" and won't duplicate.
  // Filtering on !isHidden here would make the sync unable to clear the
  // warning for hidden-only cases.
  const missingWorksheets = useMemo(() => {
    if (isStandaloneMode()) return [];
    return worksheets.filter((ws) =>
      // WFI's own sheets — the Completion Certificate (generated on
      // manager / partner sign-off), WI_Index (generated on Setup save) and
      // the XERO / XERO LY / XERO TB imports — are not workpapers and
      // shouldn't appear in the status-items table. Exclude them from the
      // warning so partners don't see spurious "sheet not included"
      // messages pointing at WFI's own output. Must stay in step with
      // syncWorksheetsToStatusItems, which uses the same predicate to
      // decide what to create — otherwise the warning would flag sheets
      // that pressing Sync Excel can never resolve.
      !isNonWorkpaperSheet(ws.name)
      && !statusItems.some((si) => si.sheetId === ws.id),
    );
  }, [worksheets, statusItems]);

  const handleSyncFromWarning = async () => {
    if (!spreadsheetId) return;
    setIsSyncingFromWarning(true);
    setSyncError(null);
    try {
      const updated = await syncWorksheetsToStatusItems(spreadsheetId);
      setStatusItems(updated);
      // Also refresh the worksheet list — the user may have hidden a sheet
      // since the page last picked it up (visibility changes don't fire the
      // worksheet-collection event), which is why sync came back empty.
      // Without this the stale-visible entry stays in `worksheets` and the
      // warning never clears.
      await refreshWorksheets();
      track("review.status_items_synced_from_warning");
    } catch (err) {
      setSyncError(getUserFriendlyErrorMessage(err));
    } finally {
      setIsSyncingFromWarning(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner label="Loading review..." />
      </div>
    );
  }

  if (error && !reviewInfo) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Text>{error}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Title3>Workbook Review</Title3>

      {/* Signed banners — shown above the tabs as global review state */}
      {managerAlreadySigned && (
        <div className={styles.signedBanner}>
          <div className={styles.signedContent}>
            <CheckmarkCircle24Regular />
            <div className={styles.signedText}>
              <Text className={typography.bodySmallStrong}>Manager Signed</Text>
              <Text className={typography.bodySmall}>
                {reviewInfo!.signingManagerEmail} — {new Date(reviewInfo!.signingManagerTime!).toLocaleString()}
              </Text>
            </div>
          </div>
        </div>
      )}
      {partnerAlreadySigned && (
        <div className={styles.signedBanner}>
          <div className={styles.signedContent}>
            <CheckmarkCircle24Regular />
            <div className={styles.signedText}>
              <Text className={typography.bodySmallStrong}>Partner Signed</Text>
              <Text className={typography.bodySmall}>
                {reviewInfo!.signingPartnerEmail} — {new Date(reviewInfo!.signingPartnerTime!).toLocaleString()}
              </Text>
            </div>
          </div>
        </div>
      )}
      {secondaryPartnerAlreadySigned && (
        <div className={styles.signedBanner}>
          <div className={styles.signedContent}>
            <CheckmarkCircle24Regular />
            <div className={styles.signedText}>
              <Text className={typography.bodySmallStrong}>Secondary Partner Signed</Text>
              <Text className={typography.bodySmall}>
                {reviewInfo!.secondarySigningPartnerEmail} — {new Date(reviewInfo!.secondarySigningPartnerTime!).toLocaleString()}
              </Text>
            </div>
          </div>
        </div>
      )}

      {/* Worksheets without status items — surfaces sheets that would drop
          off the completion certificate and won't appear in any status
          tracking. Shown to both Manager and Partner tabs, and only while
          there's still a chance to fix it (pre-partner-sign). Copy avoids
          the internal "status item" term — partners just need to know that
          some sheets aren't included in the review. */}
      {missingWorksheets.length > 0 && !partnerAlreadySigned && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>
              {missingWorksheets.length === 1
                ? "1 sheet isn't included in this review"
                : `${missingWorksheets.length} sheets aren't included in this review`}
            </MessageBarTitle>
            {" "}
            These sheets are in the workbook but haven't been added to the review, so they won't appear on the completion certificate. Affected:
            {" "}
            {missingWorksheets.map((w) => `"${w.name}"`).join(", ")}. Click Sync Excel to include them.
          </MessageBarBody>
          <MessageBarActions>
            <Button
              size="small"
              onClick={handleSyncFromWarning}
              disabled={isSyncingFromWarning || !spreadsheetId}
            >
              {isSyncingFromWarning ? "Syncing..." : "Sync Excel"}
            </Button>
          </MessageBarActions>
        </MessageBar>
      )}
      {syncError && (
        <Text className={typography.errorText}>{syncError}</Text>
      )}

      <TabList
        selectedValue={selectedTab}
        onTabSelect={(_e, data) => setSelectedTab(data.value as TabValue)}
      >
        <Tab value="manager">Manager</Tab>
        <Tab value="partner">Partner</Tab>
      </TabList>

      {selectedTab === "manager" ? (
        <ManagerTab
          spreadsheetId={spreadsheetId}
          userRole={userRole}
          reviewInfo={reviewInfo}
          discussions={discussions}
          statusItems={statusItems}
          workbookBlockReason={workbookBlockReason}
          onReviewInfoUpdated={setReviewInfo}
        />
      ) : (
        <PartnerTab
          spreadsheetId={spreadsheetId}
          userRole={userRole}
          reviewInfo={reviewInfo}
          discussions={discussions}
          statusItems={statusItems}
          workbookBlockReason={workbookBlockReason}
          onReviewInfoUpdated={setReviewInfo}
        />
      )}
    </div>
  );
};

export default Review;
